import { Timestamp } from 'firebase/firestore';
import type { CamposFichaAtivo, Equipamento, PropriedadeAtivo } from '../../../../models';
import { normalizarPatrimonio } from '../../../../models';
import { dataInputParaDate, timestampParaDataInput } from '../../../../utils/dataInput';

/** Estado do formulário da ficha — tudo texto (inputs controlados); convertido na hora de salvar. */
export type FormFichaAtivo = Record<keyof CamposFichaAtivo | 'tipo', string>;

const CAMPOS_TEXTO: Array<keyof CamposFichaAtivo> = [
  'patrimonio',
  'nSerie',
  'modelo',
  'fabricante',
  'sistemaOperacional',
  'processador',
  'memoria',
  'tamanhoDisco',
  'colaboradorAtualId',
  'clienteAtualId',
  'clienteProprietarioId',
  'projetoClienteId',
  'notaFiscalNumero',
  'fornecedor',
  'linha',
  'operadora',
  'msin',
  'pin1',
  'puk1',
  'pin2',
  'puk2',
  'numeroCartao',
];

/** "1.234,56" (pt-BR), "3.500" (milhar) ou "1234.56" → número. Com vírgula, pontos são
 * milhar; sem vírgula, ponto seguido de grupos de 3 dígitos também é milhar ("3.500" =
 * 3500), senão é decimal. Vazio → null; inválido → NaN. */
export function lerValorMonetario(texto: string): number | null {
  const limpo = texto.trim().replace(/\s|R\$/g, '');
  if (!limpo) return null;
  if (limpo.includes(',')) return Number(limpo.replace(/\./g, '').replace(',', '.'));
  if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) return Number(limpo.replace(/\./g, ''));
  return Number(limpo);
}

export function formVazio(): FormFichaAtivo {
  const form = { tipo: '', propriedade: '', dataCompra: '', valor: '' } as FormFichaAtivo;
  CAMPOS_TEXTO.forEach((campo) => (form[campo] = ''));
  return form;
}

export function formDoEquipamento(equipamento: Equipamento): FormFichaAtivo {
  const form = formVazio();
  form.tipo = equipamento.tipo ?? '';
  CAMPOS_TEXTO.forEach((campo) => (form[campo] = ((equipamento as unknown as Record<string, unknown>)[campo] as string) ?? ''));
  form.propriedade = equipamento.propriedade ?? '';
  form.dataCompra = timestampParaDataInput(equipamento.dataCompra);
  form.valor = equipamento.valor != null ? String(equipamento.valor) : '';
  return form;
}

/** Form → campos pra gravar ('' vira null; patrimônio normalizado; valor aceita "1.234,56"). */
export function camposDoForm(form: FormFichaAtivo): CamposFichaAtivo & { tipo: string } {
  const campos = { tipo: form.tipo } as CamposFichaAtivo & { tipo: string };
  CAMPOS_TEXTO.forEach((campo) => {
    const valor = form[campo].trim();
    (campos as unknown as Record<string, unknown>)[campo] = valor || null;
  });
  campos.patrimonio = normalizarPatrimonio(form.patrimonio);
  campos.propriedade = (form.propriedade || null) as PropriedadeAtivo | null;
  if (campos.propriedade !== 'cliente') campos.clienteProprietarioId = null;
  // Ativo do Cliente: o Projeto é o próprio Cliente (a ficha nem pergunta de novo).
  if (campos.propriedade === 'cliente') campos.projetoClienteId = campos.clienteProprietarioId ?? null;
  else if (campos.propriedade !== 'raro') campos.projetoClienteId = null; // campo nem aparece sem Propriedade
  campos.dataCompra = form.dataCompra ? Timestamp.fromDate(dataInputParaDate(form.dataCompra)) : null;
  const valor = lerValorMonetario(form.valor);
  campos.valor = valor != null && Number.isFinite(valor) ? valor : null;
  return campos;
}

/** Validação mínima de cadastro — o resto vira pendência (não bloqueia salvar). */
export function erroDoForm(form: FormFichaAtivo): string | null {
  if (!form.tipo) return 'Escolha o tipo do ativo.';
  if (!normalizarPatrimonio(form.patrimonio)) return 'Informe o identificador (patrimônio / nº de inventário).';
  if (!form.propriedade) return 'Informe a propriedade (Raro ou Cliente).';
  if (form.propriedade === 'cliente' && !form.clienteProprietarioId) return 'Informe de qual Cliente é o ativo.';
  const valor = lerValorMonetario(form.valor);
  if (valor != null && !Number.isFinite(valor)) return 'Valor inválido.';
  return null;
}
